import React, { useCallback } from "react";
import {
  EntityChipShell,
  useInsidePreviewPopup,
  type Color,
  type EntityAvatarSize,
  type EntityIconStyle,
} from "@salestrak/ui";

import {
  entityDataEqual,
  resolveEntityLabel,
  resolveEntityVisual,
  type EntityData,
} from "#app/lib/entity-visuals";

import { EntityTagBody, type EntityLabelInk } from "./entity-tag-body";
import { EntityPreview, hasEntityPreview } from "./entity-preview";
import { EntityVisual, type EntityVisualSize } from "./entity-visual";
import { useEntityDetailOptional } from "./entity-detail-provider";
import type { Omittable } from "@salestrak/ui";

// Chips are props-only — preview/dialog hooks mount lazily on open.
// `interactive` defaults TRUE; pass `interactive={false}` when the parent owns
// the click.

export type EntityTagProps = Omittable<{
  entity: EntityData;
  variant?: "plain" | "chip";
  color?: Color;
  display?: "full" | "visual" | "label";
  weight?: "regular" | "medium" | "semibold";
  size?: EntityVisualSize;
  /** Size the VISUAL one step off the label's `size` (label text keeps
   *  `size`) — e.g. the kanban column header's 16px glyph beside 14px text.
   *  Defaults to `size`. */
  visualSize?: EntityVisualSize;
  /** Override the derived visual↔label gap (the kanban column header's 4px
   *  glyph↔text rhythm). Defaults to the size-derived gap. */
  visualGap?: "xs" | "sm";
  /** Pin the avatar circle to an exact Avatar size, off the semantic map. */
  avatarSize?: EntityAvatarSize;
  /** Fixed visual-rail box (px): every kind's visual occupies exactly this
   *  square (all entity visuals center in it). See EntityVisual.boxSize. */
  visualBoxSize?: number;
  subtitle?: boolean;
  /** Stack the subtitle below the label (identity-card layout) instead of
   *  inline after it. */
  subtitleStacked?: boolean;
  /** Secondary line(s) rendered below the label (e.g. a search result's
   *  address). An array stacks one clamped line per entry — for values that
   *  are each free text (a job title over a role name), so a long one clips
   *  alone instead of eating its neighbour. */
  description?: string | readonly string[];
  /** A kind word above the label (a member's role) as a tracked-caps
   *  kindLabel — the quietest line, on the far side of the name from the
   *  description. */
  kindLabel?: string;
  /** Muted context inline after the label (a search row's
   *  `in <location> · 3d ago`). Composes with `description`. */
  meta?: string;
  /** The SalesTrak author sentinel; ⇒ never interactive. */
  system?: boolean;
  /** Live presence dot on member avatars — OPT-IN, off by default (see
   *  EntityVisual.presence for the surface policy). */
  presence?: boolean;
  interactive?: boolean;
  /** Hover preview, independent of the click. Defaults to `interactive`;
   *  force `true` on a chip whose parent owns the press (a table Name cell)
   *  so the card still opens on the name while clicks bubble to the cell.
   *  Only kinds with a card honor it (`hasEntityPreview`): a stage, workflow,
   *  project type or source chip never previews, whatever this says. */
  preview?: boolean;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  /** Anchored confirm popover before clicks that navigate away. */
  confirmOnClick?: {
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
  };
  as?: "box" | "text";
  padded?: boolean;
  /** Ghost hover wash; always forced off for `display="label"`. */
  ghost?: boolean;
  /** Underline on hover instead of a padded wash — the inline, zero-padding
   *  treatment for a name that sits in a sentence (with a hover preview). */
  underlineHover?: boolean;
  /** The quiet rung the label reads at — `muted` for a de-emphasized value
   *  (selector trigger values), `faint` for a row the eye should pass over
   *  (read inbox rows). A former member's label is muted regardless. */
  labelInk?: EntityLabelInk;
  /** Force-mute the visual glyph (icon kinds) to the muted tone — the
   *  quiet leading rail of a de-emphasized list (inbox rows). */
  mutedVisual?: boolean;
  /** Line height for the primary label inside a box chip. */
  labelLeading?: "none" | "tight" | "normal";
  /** Layered icon treatment for padded identity glyphs. */
  iconStyle?: EntityIconStyle;
}>;

function EntityTagComponent({
  entity,
  variant = "plain",
  color,
  display = "full",
  weight,
  size = "sm",
  visualSize,
  visualGap,
  avatarSize,
  visualBoxSize,
  subtitle,
  subtitleStacked,
  description,
  kindLabel,
  meta,
  system,
  presence,
  interactive = true,
  preview,
  onClick,
  confirmOnClick,
  as = "box",
  padded,
  ghost = true,
  underlineHover,
  labelInk,
  mutedVisual,
  labelLeading,
  iconStyle,
}: EntityTagProps) {
  const detail = useEntityDetailOptional();
  // A preview is one layer deep: a chip inside a card is click-only (the
  // shell refuses to arm a card from inside one), so its clipped lines hand
  // back their text through the tooltip a previewing chip would forgo.
  const insidePreview = useInsidePreviewPopup();

  const isInteractive = interactive && !system;
  // A member the viewer may not open has no hover card either — the card is
  // a profile excerpt (see `canOpenEntity`).
  const profileWithheld =
    entity.kind === "user" && detail?.canOpenEntity("user", entity.id) === false;
  const showPreview =
    (preview ?? isInteractive) &&
    hasEntityPreview(entity) &&
    !system &&
    !profileWithheld &&
    !insidePreview;

  const resolvedWeight = weight ?? (display === "label" ? "semibold" : "medium");
  const ghostEnabled = display === "label" ? false : ghost;
  const label = system ? "SalesTrak" : resolveEntityLabel(entity);

  const defaultClick =
    isInteractive && !onClick && detail?.canOpenEntity(entity.kind, entity.id)
      ? () => detail.openEntity(entity.kind, entity.id)
      : undefined;
  const clickAction = isInteractive ? (onClick ?? defaultClick) : undefined;

  const clickConfirmation =
    confirmOnClick && clickAction ? confirmOnClick : undefined;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // Interactive tags live inside clickable rows/cards — never double-fire.
      e?.stopPropagation?.();
      clickAction?.(e);
    },
    [clickAction],
  );

  const subtitleText =
    subtitle && entity.kind === "user"
      ? (entity.user?.username ??
        (entity.user?.name ? (entity.user?.email ?? null) : null))
      : null;

  // A former member's label mutes off the same resolved visual the avatar
  // dims on, so text and avatar can't disagree about whether a user is still
  // a member. An explicit rung wins — a read row's faint stays faint.
  const entityVisual = resolveEntityVisual(entity);
  const resolvedLabelInk =
    labelInk ??
    (entityVisual.type === "avatar" && entityVisual.former ? "muted" : undefined);

  const visual =
    display === "label" ? undefined : (
      <EntityVisual
        entity={entity}
        size={visualSize ?? size}
        avatarSize={avatarSize}
        boxSize={visualBoxSize}
        padded={padded}
        system={system}
        presence={presence}
        muted={mutedVisual}
        iconStyle={iconStyle}
        // Beside its label the visual only decorates it.
        decorative={display === "full"}
      />
    );

  const renderPreview = showPreview
    ? ({ close }: { close: () => void }) => (
        <EntityPreview entity={entity} onNavigate={close} />
      )
    : undefined;

  return (
    <EntityChipShell
      variant={variant}
      color={color}
      as={as}
      // A preview-only chip stays enabled so hover can open the card. With
      // neither a click nor a card — non-interactive, or an interactive chip
      // whose entity the viewer may not open — the shell renders it inert
      // (no button role, no ghost).
      disabled={!clickAction && !showPreview}
      onClick={clickAction ? handleClick : undefined}
      confirmOnClick={clickConfirmation}
      renderPreview={renderPreview}
      ghost={ghostEnabled}
      underlineHover={underlineHover}
      // Small chips tighten the visual↔label gap so it stays proportional to
      // the glyph — derived here once; `visualGap` is the explicit opt-out
      // for surfaces with their own rhythm (kanban column header: 4px).
      visualGap={visualGap ?? (size === "2xs" || size === "xs" ? "xs" : "sm")}
    >
      <EntityTagBody
        label={label}
        visual={visual}
        display={display}
        weight={resolvedWeight}
        size={size}
        subtitle={subtitleText}
        subtitleStacked={subtitleStacked}
        description={description}
        // A chip with no preview card has no other disclosure for a clipped
        // line, so its description lines hand back the rest on hover; a
        // previewing chip keeps the card as its one hover layer.
        descriptionOverflowTooltip={!showPreview}
        kindLabel={kindLabel}
        meta={meta}
        labelInk={resolvedLabelInk}
        labelLeading={labelLeading}
        as={as}
      />
    </EntityChipShell>
  );
}

EntityTagComponent.displayName = "EntityTag";

// `entity` is value-compared structurally because call sites pass fresh inline
// objects every render; all other props are reference-compared.
function propsEqual(prev: EntityTagProps, next: EntityTagProps): boolean {
  if (!entityDataEqual(prev.entity, next.entity)) return false;

  const prevKeys = Object.keys(prev) as (keyof EntityTagProps)[];
  const nextKeys = Object.keys(next) as (keyof EntityTagProps)[];
  // Equal key counts plus every prev key matching covers a key present on only
  // one side, without allocating a Set per comparison.
  if (prevKeys.length !== nextKeys.length) return false;
  for (const key of prevKeys) {
    if (key === "entity") continue;
    if (!Object.is(prev[key], next[key])) return false;
  }
  return true;
}

export const EntityTag = React.memo(EntityTagComponent, propsEqual);
